import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';

import {
  catchError, from, NEVER, Observable, switchMap, take, throwError,
} from 'rxjs';

import { safeJson } from '../helpers/json.helper';

import { AuthService } from './auth.service';


const SESSION_ENDED: Record<number, string> = {
  401: 'Auth.RoleChanged',
  403: 'Auth.UserDeactivated',
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  return authService.user$.pipe(
    take(1),
    switchMap(user => {
      if (user && user.token) {
        if (user.tokenExpiration && user.tokenExpiration < Date.now() / 1000) {
          return authService.refreshToken().pipe(
            switchMap(user => {
              if (user && user.token) {
                const cloned = setAuthHeader(req, user.token);

                return next(cloned);
              }

              return next(req);
            }),
          );
        } else {
          const cloned = setAuthHeader(req, user.token);

          return next(cloned);
        }
      }

      return next(req);
    }),
    catchError((error: unknown) => signOutWhenSessionEnded(error, authService)),
  );
};


const signOutWhenSessionEnded = (
  error: unknown,
  authService: AuthService,
): Observable<never> => {
  if (!(error instanceof HttpErrorResponse)) return throwError(() => error);

  const endsSession = SESSION_ENDED[error.status];
  if (!endsSession) return throwError(() => error);

  return from(problemTitle(error)).pipe(
    switchMap((title) => {
      if (title !== endsSession) return throwError(() => error);

      // logout() drops the user synchronously, so a screen that fired several requests at
      // once signs out on the first failure back and the rest fall through here.
      if (authService.getUser()) {
        authService.logout();
      }

      return NEVER;
    }),
  );
};

const setAuthHeader = (req: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
  return req.clone({
    headers: req.headers.set('Authorization', `Bearer ${token}`),
  });
};

const problemTitle = async(error: HttpErrorResponse): Promise<string | undefined> => {
  const body: unknown = error.error;
  const raw = body instanceof Blob ? await body.text() : body;

  if (typeof raw === 'string') {
    const parsed = safeJson<{ title?: string }>(raw);

    return parsed.ok ? parsed.value?.title : undefined;
  }

  return (raw as { title?: string } | null)?.title;
};
